// components/Generator/Generator.tsx
"use client";
import React, { useState, useEffect, useRef, SyntheticEvent } from "react";
import {
  Text,
  Flex,
  Button,
  Grid,
  Image,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Textarea,
  useToast,
  Switch,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  SliderMark,
  Icon,
  createIcon,
  CloseButton,
  useMediaQuery,
} from "@chakra-ui/react";
import { handleWebSocketError } from "../../../utils";
import LoraSelector from "./LoraSelector";
import { FaTrash } from "react-icons/fa";
import Link from "next/link";
import { IoMdClose } from "react-icons/io";
import { BsEraser } from "react-icons/bs";
import { useAppContext } from "hooks";
import {
  useGetModelCreation,
  useGetCustomPromptMaxLength,
  useGetImgGenModePrice,
  useDeleteImage,
} from "queries";
import audio from "@public/case-closed-531.mp3";
import { useRouter } from "next/navigation";
import { Checkpoint, OptionCategory, Option, RemixGalleryImage } from "types";
import { WSS_IMG_GEN } from "api";
import { getImageSize, useImageSize } from "react-image-size";
import heic2any from "heic2any";
import FilterOption from "../FilterOption";
import NsfwImage from "../NsfwImage";
import CircularProgressCustom from "./CircularProgressCustom";
import PromptModal from "../PromptModal";
import RemixGalleryLayout from "../RemixGalleryLayout";
import { useDropzone, FileRejection } from "react-dropzone";
import UploadRemixImageIcon from "@public/upload_remix_image_icon.svg";
import { Stage, Layer, Line, Circle } from "react-konva";
import Konva from "konva";

interface DisplayedOptions {
  [option_category_name: string]: string[];
}

interface Generation {
  sas_url: string;
  file_name: string;
  upscaled: boolean;
  status: string;
  nsfw?: boolean | null;
  img2img?: boolean
}

interface ImageGenerationOptionRequest {
  name: string;
  option_category_name: string;
}

interface ImageGenerationRequest {
  checkpoint_name: string;
  options: ImageGenerationOptionRequest[] | null;
  custom_prompt: string | null;
  custom_negative_prompt: string | null;
  hack_to_digital: boolean;
  quick_mode: boolean;
  ratio: string;
  fix_face: boolean;
}

interface ImageGenerationRemixRequest {
  checkpoint_name: string;
  remix_gallery_image_id: string | null;
  upload_image_b64image_str: string | null;
  mask_b64image_str: string | null;
  similarity: number;
  custom_prompt: string | null;
  custom_negative_prompt: string | null;
  fix_face: boolean;
  copy_pose: boolean;
}

interface UpscaleFromImageRequest {
  file_name: string;
}

interface QueueInfo {
  queue_no: number;
  estimted_time: number;
  estimated_remaining_time: number;
  estimated_percentage: number;
  low_priority: boolean;
}

interface LineState {
  tool: string;
  strokeWidth: number;
  points: number[];
}

interface StageDimensions {
  width: number;
  height: number;
}

interface CursorPosition {
  x: number;
  y: number;
}

interface Creation {
  sas_url: string;
  file_name: string;
  upscaled: boolean;
  status: string;
  nsfw?: boolean | null;
}

export const Generator = ({
  checkpoints,
  gender,
  id,
}: {
  checkpoints: Checkpoint[];
  gender: string;
  id: string;
}) => {
  const [selectedMainKey, setSelectedMainKey] = useState<string | null>(null);
  const [selectedNormalOptions, setSelectedNormalOptions] = useState<{
    [option_category_name: string]: string;
  }>({});
  const [selectedQuickOption, setSelectedQuickOption] = useState<{
    option_category_name: string;
    option_name: string;
  }>({ option_category_name: "", option_name: "" });
  const toast = useToast();
  const [selectedCheckpoint, setSelectedCheckpoint] =
    useState<Checkpoint | null>(null);
  const [upscalingImageFileName, setUpscalingImageFileName] = useState<
    string | null
  >(null);
  const [displayedNormalOptions, setDisplayedNormalOptions] =
    useState<DisplayedOptions>({});
  const [displayedQuickOptions, setDisplayedQuickOptions] =
    useState<DisplayedOptions>({});
  const [imageUrl, setImageUrl] = useState<string>("stale");
  const { jwt, profile } = useAppContext();
  const { onOpen, isOpen, onClose } = useDisclosure();
  const [menuStates, setMenuStates] = useState<{ [key: string]: boolean }>({});
  const { data: generations, refetch } = useGetModelCreation(8, id);
  const router = useRouter();
  const [updatedGenerations, setUpdatedGenerations] = useState<Generation[]>(
    []
  );
  const [customPrompt, setCustomPrompt] = useState<string>("");
  const [customPromptOpen, setCustomPromptOpen] = useState<boolean>(false);
  const [customNegativePrompt, setCustomNegativePrompt] = useState<string>("");
  const [customNegativePromptOpen, setCustomNegativePromptOpen] =
    useState<boolean>(false);
  const [isWebSocketProcessing, setIsWebSocketProcessing] = useState(false);
  const [creationMode, setCreationMode] = useState<string>("Normal");
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);
  const [numberOfGenerations, setNumberOfGenerations] = useState<number>(1);
  const [nthGeneration, setNthGeneration] = useState<number>(1);
  const [upscalingImageAspectRatio, setUpscalingImageAspectRatio] =
    useState<number>(2 / 3);
  const {
    onOpen: onOpenPreview,
    onClose: onClosePreview,
    isOpen: isOpenPreview,
  } = useDisclosure();
  const [isInsufficientBalance, setIsInsufficientBalance] =
    useState<boolean>(false);
  const isUserModelPath = location.pathname.includes("/mymodels");
  const MAX_COLUMNS_XL = 5;
  const MAX_COLUMNS_2XL = 6;
  const MAX_OPTIONS = 13;
  const deleteImage = useDeleteImage();
  const [is2XL] = useMediaQuery("(min-width: 1536px)");
  const [isXL] = useMediaQuery("(min-width: 1280px)");
  const [isMobile] = useMediaQuery("(max-width: 768px)");
  const { data: customPromptMaxLength } = useGetCustomPromptMaxLength();
  const [isFixFace, setIsFixFace] = useState<boolean>(true);
  const [isCopyPose, setIsCopyPose] = useState<boolean>(false);
  const { data: imgGenModePrice } = useGetImgGenModePrice();
  const [creditsRequired, setCreditsRequired] = useState<number>(1);
  const [dimensions] = useImageSize(selectedImageUrl ?? "");
  const [selectedImageAspectRatio, setSelectedImageAspectRatio] =
    useState<number>(2 / 3);
  const [ratio, setRatio] = useState<string>("portrait");
  const [queueInfo, setQueueInfo] = useState<QueueInfo | null>(null);
  const [similarity, setSimilarity] = useState<number>(0.5);
  const [remixGalleryImage, setRemixGalleryImage] =
    useState<RemixGalleryImage | null>(null);
  const [remixImageFile, setRemixImageFile] = useState<File | null>(null);
  const [remixImageFileB64Plain, setRemixImageFileB64Plain] = useState<
    string | null
  >(null);
  const [remixImagePreview, setRemixImagePreview] = useState<string | null>(
    null
  );
  const { isOpen: isPromptModalOpen, onOpen: openPromptModal, onClose: closePromptModal } = useDisclosure();
  const [selectedImage, setSelectedImage] = useState<Generation>();
  const {
    isOpen: isDeleteOpen,
    onOpen: deleteOpen,
    onClose: deleteClose,
  } = useDisclosure();
  const [generatedImage, setGeneratedImage] = useState<Generation | null>(null);

  const onDrop = async (
    acceptedFiles: File[],
    fileRejections: FileRejection[]
  ) => {
    if (!isWebSocketProcessing) {
      if (acceptedFiles.length === 1) {
        setRemixImageFile(acceptedFiles[0]);
      }

      if (fileRejections.length > 1) {
        toast({
          title: "Error",
          description: "Please upload one and only one image",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } else if (fileRejections.length === 1) {
        toast({
          title: "Error",
          description: fileRejections[0].errors[0].message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  const { getRootProps, getInputProps, isDragActive, inputRef } = useDropzone({
    onDrop,
    accept: {
      "image/png": [],
      "image/jpeg": [],
      "image/webp": [],
      "image/heic": [],
    },
    maxSize: 3 * 1024 * 1024,
    multiple: false,
  });

  const isValidURL = (urlToTest: string): boolean => {
    try {
      const url = new URL(urlToTest);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch (error) {
      return false;
    }
  };

  useEffect(() => {
    const onPaste = async (event: ClipboardEvent) => {
      if (event.clipboardData !== null && !isWebSocketProcessing) {
        if (event.clipboardData.files && inputRef.current !== null) {
          (inputRef.current as unknown as HTMLInputElement).files =
            event.clipboardData.files;
          inputRef.current.dispatchEvent(
            new Event("change", { bubbles: true })
          );
        }

        const url = event.clipboardData.getData("text");

        if (url && isValidURL(url) && inputRef.current !== null) {
          await fetch(url)
            .then((res) => res.blob())
            .then(async (blob): Promise<Blob | Blob[]> => {
              if (
                ["image/png", "image/jpeg", "image/webp"].includes(blob.type)
              ) {
                return blob;
              }

              return await heic2any({
                blob,
              });
            })
            .then((conversionResult: Blob | Blob[]) => {
              if (conversionResult instanceof Blob) {
                const blob: any = conversionResult;
                blob.lastModifiedDate = new Date();
                blob.name = `image.${conversionResult.type.split("/").pop()}`;
                setRemixImageFile(blob as File);
              } else {
                if (conversionResult.length > 0) {
                  const blob: any = conversionResult[0];
                  blob.lastModifiedDate = new Date();
                  blob.name = `image.${conversionResult[0].type
                    .split("/")
                    .pop()}`;
                  setRemixImageFile(blob as File);
                }
              }
            })
            .catch(() => {
              toast({
                title: "Error",
                description: "The pasted image URL is invalid",
                status: "error",
                duration: 5000,
                isClosable: true,
              });
            });
        }
      }
    };

    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, []);

  const {
    isOpen: isRemixGalleryOpen,
    onOpen: onRemixGalleryOpen,
    onClose: onRemixGalleryClose,
  } = useDisclosure();

  let maxImagesToShow: number;

  if (is2XL) {
    maxImagesToShow = MAX_COLUMNS_2XL - 1;
  } else if (isXL) {
    maxImagesToShow = MAX_COLUMNS_XL - 1;
  } else {
    maxImagesToShow = MAX_COLUMNS_XL - 1;
  }

  useEffect(() => {
    if (queueInfo !== null) {
      console.log(queueInfo);
    }
  }, [queueInfo]);

  useEffect(() => {
    if (remixGalleryImage !== null) {
      if (remixImagePreview !== null && remixImageFile !== null) {
        URL.revokeObjectURL(remixImagePreview);
      }

      setRemixImageFile(null);
      setRemixImageFileB64Plain(null);
      setRemixImagePreview(remixGalleryImage.sas_url);
    }
  }, [remixGalleryImage]);

  const validateRemixImageObject = async (
    objectURL: string
  ): Promise<boolean> => {
    try {
      const dimensions = await getImageSize(objectURL);

      if (dimensions.width < 512 || dimensions.height < 512) {
        toast({
          title: "Error",
          description: "Image size too small, at min 512x512",
          status: "error",
          duration: 5000,
          isClosable: true,
        });

        return false;
      }

      const portraitRatio = dimensions.height / dimensions.width;

      if (portraitRatio < 0.5 || portraitRatio > 2) {
        toast({
          title: "Error",
          description:
            "Please ensure your image ratio is no wider than 2:1 or taller than 1:2",
          status: "error",
          duration: 5000,
          isClosable: true,
        });

        return false;
      }

      return true;
    } catch (error) {
      toast({
        title: "Error",
        description: "Invalid image",
        status: "error",
        duration: 5000,
        isClosable: true,
      });

      console.error(error);
      return false;
    }
  };

  useEffect(() => {
    if (remixImageFile !== null) {
      const processImageFile = async () => {
        if (remixImagePreview !== null && remixGalleryImage === null) {
          URL.revokeObjectURL(remixImagePreview);
        }

        setRemixGalleryImage(null);
        let objectURL = URL.createObjectURL(remixImageFile);

        // Convert heic to png for preview if mimetype empty or filename include .heic
        if (
          remixImageFile.type === "" ||
          remixImageFile.name.toLowerCase().includes(".heic")
        ) {
          await fetch(objectURL)
            .then((res) => res.blob())
            .then((blob) =>
              heic2any({
                blob,
              })
            )
            .then((conversionResult: Blob | Blob[]) => {
              if (conversionResult instanceof Blob) {
                URL.revokeObjectURL(objectURL);
                objectURL = URL.createObjectURL(conversionResult);
              } else {
                if (conversionResult.length > 0) {
                  URL.revokeObjectURL(objectURL);
                  objectURL = URL.createObjectURL(conversionResult[0]);
                }
              }
            })
            .catch((e) => {
              console.log(e);
            });
        }

        const isRemixImageObjectValid = await validateRemixImageObject(
          objectURL
        );

        if (!isRemixImageObjectValid) {
          setRemixImageFile(null);
          setRemixImageFileB64Plain(null);
          setRemixImagePreview(null);
          URL.revokeObjectURL(objectURL);
          return;
        }

        setRemixImagePreview(objectURL);
        const fileReader = new FileReader();
        fileReader.readAsDataURL(remixImageFile);

        fileReader.onload = () => {
          if (fileReader.result !== null) {
            const b64DataURI =
              fileReader.result instanceof ArrayBuffer
                ? new TextDecoder().decode(fileReader.result)
                : fileReader.result;

            const b64Plain = b64DataURI.split(",")[1];
            setRemixImageFileB64Plain(b64Plain);
          }
        };

        fileReader.onerror = (error) => {
          console.log("Error: ", error);
        };
      };

      processImageFile();
    }
  }, [remixImageFile]);

  useEffect(() => {
    if (imageUrl !== "stale" && imageUrl !== "loading") {
      setQueueInfo(null);
    }
  }, [imageUrl]);

  useEffect(() => {
    if (dimensions) {
      setSelectedImageAspectRatio(dimensions.width / dimensions.height);
    } else {
      setSelectedImageAspectRatio(2 / 3);
    }
  }, [dimensions]);

  useEffect(() => {
    setCreditsRequired(numberOfGenerations * (imgGenModePrice?.default ?? 1));
  }, [numberOfGenerations, imgGenModePrice]);

  useEffect(() => {
    setIsInsufficientBalance(profile?.credit! - creditsRequired < 0);
  }, [profile, creditsRequired]);

  useEffect(() => {
    if (checkpoints !== null && checkpoints?.length > 0) {
      const filteredCheckpoints = checkpoints.filter(
        (checkpoint) => checkpoint.style.name === "realistic"
      );

      if (filteredCheckpoints.length === 1) {
        setSelectedMainKey(filteredCheckpoints[0].style.name);
      } else {
        setSelectedMainKey(checkpoints[0].style.name);
      }
    }
  }, [checkpoints, creationMode]);

  useEffect(() => {
    setCustomPrompt(creationMode === "Remix" ? gender : "");
  }, [creationMode]);

  useEffect(() => {
    if (checkpoints !== null && selectedMainKey !== null) {
      const checkpoint = checkpoints.find(
        (checkpoint: Checkpoint) => checkpoint.style.name === selectedMainKey
      );

      if (checkpoint !== undefined) {
        setSelectedCheckpoint(checkpoint);
      }
    }
  }, [checkpoints, selectedMainKey]);

  useEffect(() => {
    if (
      selectedCheckpoint !== null &&
      selectedCheckpoint.style.option_categories !== null &&
      selectedCheckpoint.style.option_categories?.length > 0
    ) {
      const initialDisplayedNormalOptions: DisplayedOptions = {};
      const initialDisplayedQuickOptions: DisplayedOptions = {};

      selectedCheckpoint.style.option_categories.forEach(
        (option_category: OptionCategory) => {
          if (option_category.options !== null) {
            if (option_category.quick_mode) {
              initialDisplayedQuickOptions[option_category.name] =
                option_category.options
                  .map((option) => option.name)
                  .slice(0, MAX_OPTIONS);
            } else {
              initialDisplayedNormalOptions[option_category.name] =
                option_category.options
                  .map((option) => option.name)
                  .slice(0, MAX_OPTIONS);
            }
          }
        }
      );

      setDisplayedQuickOptions(initialDisplayedQuickOptions);
      setDisplayedNormalOptions(initialDisplayedNormalOptions);

      const firstQuickOptionCategory =
        selectedCheckpoint.style.option_categories.find(
          (option_category: OptionCategory) =>
            option_category.options !== null &&
            option_category.options.length > 0 &&
            option_category.quick_mode
        );

      if (
        firstQuickOptionCategory &&
        firstQuickOptionCategory.options !== null &&
        firstQuickOptionCategory.options.length > 0
      ) {
        setSelectedQuickOption({
          option_category_name: firstQuickOptionCategory.name,
          option_name: firstQuickOptionCategory.options[0].name,
        });
      }

      setSelectedNormalOptions(
        Object.fromEntries(
          selectedCheckpoint.style.option_categories.reduce(
            (accumulator: string[][], option_category: OptionCategory) => {
              if (
                option_category.options !== null &&
                option_category.options.length > 0 &&
                !option_category.quick_mode
              ) {
                accumulator.push([
                  option_category.name,
                  option_category.options[0].name,
                ]);
              }

              return accumulator;
            },
            []
          )
        )
      );

      setCustomNegativePrompt(selectedCheckpoint.style.negative_prompt);
    }
  }, [selectedCheckpoint]);

  useEffect(() => {
    if (generations && (generations.pages[0] as any)?.image_generations) {
      setUpdatedGenerations((generations.pages[0] as any)?.image_generations);
    }
  }, [generations]);

  const handleCreationMode = (mode: string) => {
    if (!isWebSocketProcessing) {
      setCreationMode(mode);
    }
  };

  const handleLoraSelect = (loraString: string) => {
    setCustomPrompt((prevPrompt) => prevPrompt + ' ' + loraString);
  };

  const toggleMenu = (menuName: string) => {
    setMenuStates((prevStates) => ({
      ...prevStates,
      [menuName]: !prevStates[menuName],
    }));
  };

  const notificationSound = new Audio(audio);


  function generationNotification(imageIsReady: boolean) {
    if (isMobile) {
      return;
    }

    if (!("Notification" in window)) {
      console.log("This browser does not support desktop notification");
    } else if (Notification.permission === "granted" && imageIsReady) {
      const notification = new Notification("Your generation is ready!");

      notificationSound.play();
      console.log(notification);
    } else if (Notification.permission !== "denied") {
      Notification.requestPermission().then((permission) => {
        if (permission === "granted") {
          console.log(permission);
        }
      });
    }
  }

  const generateUpscaledImage = async (
    localUpscalingImageFileName: string,
    localUpscalingImageAspectRatio: number
  ) => {
    if (isInsufficientBalance) {
      onOpen();
      return;
    }

    const upscaleMethod = selectedImage?.img2img ? "upscale" : "hires"

    setUpscalingImageFileName(localUpscalingImageFileName);
    setUpscalingImageAspectRatio(localUpscalingImageAspectRatio);

    const payloadToSend: UpscaleFromImageRequest = {
      file_name: localUpscalingImageFileName,
    };

    setIsWebSocketProcessing(true);
    onClosePreview();
    const fetchImage = async () => {
      const socket = new WebSocket(WSS_IMG_GEN(jwt, upscaleMethod));
      let isConnectionEstablished = false;

      socket.onopen = () => {
        setImageUrl("loading");
        console.log("WebSocket connection opened");
        // Scroll a little bit to the top after opening the WebSocket
        window.scrollBy({ top: -400, behavior: "smooth" });
      };

      socket.onmessage = (event) => {
        console.log("Message received from the server");
        try {
          const data = JSON.parse(event.data);

          if (
            data.message === "image generation connection established" &&
            !isConnectionEstablished
          ) {
            console.log("Connection established, sending payload");
            isConnectionEstablished = true;
            socket.send(JSON.stringify(payloadToSend));
          } else if (data.message === "image generation queue info") {
            setQueueInfo(data);
          } else if (
            data.message === "image generation progress" ||
            data.message === "image generation result"
          ) {
            const b64Image = data.b64_image;
            setImageUrl(b64Image);
            if (data.message === "image generation result") {
              generationNotification(true);

              setTimeout(() => {
                refetch();
              }, 600);
            }
          } else if (data.message === "image generation creation") {
            setGeneratedImage(data.creation as Generation);
          }
        } catch (e) {
          console.error("Error parsing message", e);
        }
      };

      socket.onerror = (error) => {
        console.error("WebSocket Error:", error);
        setImageUrl("");
        setUpscalingImageFileName(null);
        setIsWebSocketProcessing(false); // Set processing to false on error
      };

      socket.onclose = (event) => {
        console.log("WebSocket connection closed:", event);

        if (event.code === 1000) {
          setIsWebSocketProcessing(false);
          setUpscalingImageFileName(null);
          return;
        } else if (event.code === 4003) {
          toast({
            title: "File Integrity Error",
            description:
              "The required file for generating the image is corrupted or unavailable. Please try again later or contact support.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        } else if (event.code === 1011) {
          toast({
            title: "Error",
            description: (
              <Text>
                Apologies, our servers are currently down for maintenance.
                Please check our{" "}
                <a
                  target="_blank"
                  style={{ textDecoration: "underline" }}
                  href="https://discord.gg/CMwtccKthy"
                >
                  Discord
                </a>{" "}
                for further updates
              </Text>
            ),
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        } else if (event.code === 4002) {
          // If insufficient balance
          onOpen();
          setIsInsufficientBalance(true);
        } else {
          toast({
            title: "Error",
            description: event.reason,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }

        setIsWebSocketProcessing(false);
        setUpscalingImageFileName(null);
      };
    };

    fetchImage();
  };

  const generateImages = async (localCreationMode: string) => {
    if (!selectedCheckpoint) return;

    if (
      isInsufficientBalance ||
      (localCreationMode === "Upscale" && profile?.credit! - 2 < 0)
    ) {
      onOpen();
      return;
    }

    generationNotification(false);
    let imgGenMode: string;

    let payloadToSend:
      | ImageGenerationRequest
      | ImageGenerationRemixRequest
      | UpscaleFromImageRequest;

    if (localCreationMode === "Remix") {
      if (remixGalleryImage === null && remixImageFileB64Plain === null) {
        return;
      }

      imgGenMode = "remix";

      payloadToSend = {
        checkpoint_name: selectedCheckpoint.file_name,
        remix_gallery_image_id:
          remixGalleryImage === null ? null : remixGalleryImage.id,
        upload_image_b64image_str: remixImageFileB64Plain,
        mask_b64image_str: remixMaskB64DataURI
          ? remixMaskB64DataURI.split(",")[1]
          : null,
        similarity: similarity,
        custom_prompt: customPrompt,
        custom_negative_prompt: customNegativePrompt,
        fix_face: isFixFace,
        copy_pose: isCopyPose,
      };
    } else if (localCreationMode === "Upscale") {
      if (generatedImage === null) {
        return;
      }

      if (creationMode === "Remix") {
        imgGenMode = "upscale";
      } else {
        imgGenMode = "hires";
      }
      payloadToSend = {
        file_name: generatedImage.file_name,
      };
    } else {
      imgGenMode = "normal";

      payloadToSend = {
        checkpoint_name: selectedCheckpoint.file_name,
        options: null,
        custom_prompt: null,
        custom_negative_prompt: null,
        hack_to_digital:
          isUserModelPath && selectedCheckpoint.style.name === "digital",
        quick_mode: localCreationMode === "Quick",
        ratio: ratio,
        fix_face: isFixFace,
      };

      if (localCreationMode === "Normal") {
        if (!isNormalLastLevel) return;

        payloadToSend.options = Object.keys(selectedNormalOptions).reduce(
          (
            accumulator: ImageGenerationOptionRequest[],
            option_category_name: string
          ) => {
            accumulator.push({
              name: selectedNormalOptions[option_category_name],
              option_category_name: option_category_name,
            });

            return accumulator;
          },
          []
        );
      } else if (localCreationMode === "Prompt") {
        if (!customPrompt) return;

        payloadToSend.custom_prompt = customPrompt;
        payloadToSend.custom_negative_prompt = customNegativePrompt;
      } else if (localCreationMode === "Quick") {
        if (
          !selectedQuickOption.option_category_name ||
          !selectedQuickOption.option_name
        )
          return;

        payloadToSend.options = [
          {
            name: selectedQuickOption.option_name,
            option_category_name: selectedQuickOption.option_category_name,
          },
        ];
      } else {
        console.log(`${localCreationMode} not supported`);
        return;
      }
    }

    setIsWebSocketProcessing(true);

    const fetchImage = async (remainingGenerations: number) => {
      if (remainingGenerations === 0) {
        setIsWebSocketProcessing(false);
        return;
      }

      const socket = new WebSocket(WSS_IMG_GEN(jwt, imgGenMode));
      let isConnectionEstablished = false;

      socket.onopen = () => {
        console.log("WebSocket connection opened");
        setImageUrl("loading");
        setNthGeneration(numberOfGenerations - remainingGenerations + 1);
      };

      socket.onmessage = (event) => {
        console.log("Message received from the server");
        try {
          const data = JSON.parse(event.data);

          if (
            data.message === "image generation connection established" &&
            !isConnectionEstablished
          ) {
            console.log("Connection established, sending payload");
            isConnectionEstablished = true;
            socket.send(JSON.stringify(payloadToSend));
          } else if (data.message === "image generation queue info") {
            setQueueInfo(data);
          } else if (
            data.message === "image generation progress" ||
            data.message === "image generation result"
          ) {
            const b64Image = data.b64_image;
            setImageUrl(b64Image);
            if (data.message === "image generation result") {
              generationNotification(true);

              setTimeout(() => {
                refetch();
              }, 500);
            }
          } else if (data.message === "image generation creation") {
            setGeneratedImage(data.creation as Generation);
          }
        } catch (e) {
          console.error("Error parsing message", e);
        }
      };

      socket.onerror = (error) => {
        console.error("WebSocket Error:", error);
        setImageUrl("stale");
        setIsWebSocketProcessing(false);
      };

      socket.onclose = (event) => {
        console.log("WebSocket connection closed:", event);
      
        if (event.code === 1000) {
          fetchImage(remainingGenerations - 1);
        } else {
          const isInsufficientBalance = handleWebSocketError(event, toast);
          if (isInsufficientBalance) {
            onOpen();
            setIsInsufficientBalance(true);
          }
          setImageUrl("stale");
          setIsWebSocketProcessing(false);
        

        }
      };
    };

    fetchImage(localCreationMode === "Upscale" ? 1 : numberOfGenerations);
  };

  const formatStyle = (style_name: string) => {
    return (
      style_name.charAt(0).toUpperCase() + style_name.slice(1).toLowerCase()
    );
  };

  const renderImages = () => {
    return (
      <Flex
        direction="column"
        ml={{ base: "0", lg: "0" }}
        borderRadius="20px"
        w={{ base: "100%", lg: "49%" }}
        p={4}
        bg="#000000"
        mt={{ base: "20px", lg: "0px" }}
        border="1px solid rgba(255, 255, 255, 0.5)"
        justify="center"
        align="center"
        maxW="100%"
      >
        {isInsufficientBalance && imageUrl === "stale" ? (
          <>
            <Text
              fontSize={{ base: "20px", md: "24px" }}
              fontWeight="bold"
              style={{
                background:
                  "linear-gradient(to right,  #FFB5FF 0%, #1AA5D3 50%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                MozBackgroundClip: "text",
              }}
            >
              No more credits
            </Text>
            <Text mt="15px" color="#F5F5F5" fontSize={{ base: "13px", md: "14px" }}>
              You ran out of generation credits, Top up to keep generating.
            </Text>
            <Button
              onClick={() => router.push("/payments")}
              mt="20px"
              color="white"
              bg="#FF1B6D"
            >
              Top Up
            </Button>
          </>
        ) : imageUrl === "stale" ? (
          creationMode === "Normal" || creationMode === "Quick" ? (
            <Text fontSize={{ base: "20px", md: "24px" }} fontWeight="semibold">
              Choose your tags and Create
            </Text>
          ) : creationMode === "Prompt" ? (
            <Text fontSize={{ base: "20px", md: "24px" }} fontWeight="semibold">
              Prompt and Create
            </Text>
          ) : creationMode === "Remix" ? (
            <Text fontSize={{ base: "20px", md: "24px" }} fontWeight="semibold">
              Remix and Create
            </Text>
          ) : (
            <></>
          )
        ) : imageUrl === "loading" ? (
          <Flex
            direction="column"
            align="center"
            justify="center"
            height="100%"
            width="100%"
            py={{ base: "20px", md: "0" }}
          >
            {queueInfo ? (
              <>
                <CircularProgressCustom
                  percentage={queueInfo.estimated_percentage}
                  queuePosition={queueInfo.queue_no}
                />
                <Text mt={3} fontSize={{ base: "12px", md: "14px" }} color="#475467">
                  {`Generating ${nthGeneration}/${numberOfGenerations}${
                    queueInfo.low_priority ? " in low priority mode" : ""
                  }`}
                </Text>
                {queueInfo.low_priority && (
                  <>
                    <Text fontSize={{ base: "12px", md: "14px" }} color="#475467">
                      To speed up generations
                    </Text>
                    <Link href="/payments" target="_blank">
                      <Text
                        as="u"
                        fontSize={{ base: "12px", md: "14px" }}
                        fontWeight="bold"
                        color="#475467"
                      >
                        Purchase Tokens
                      </Text>
                    </Link>
                  </>
                )}
              </>
            ) : (
              <Text fontSize={{ base: "12px", md: "14px" }} color="#475467">
                {`Generating ${nthGeneration}/${numberOfGenerations}`}
              </Text>
            )}
          </Flex>
        ) : (
          <>
            <Image
              src={`data:image/jpeg;base64,${imageUrl}`}
              alt="Generated Image"
              objectFit="contain"
              w="100%"
              maxW="100%"
              borderRadius="8px"
            />
            {!isWebSocketProcessing && generatedImage !== null && (
              <Button
                mt={{ base: "10px", lg: "20px" }}
                bg="transparent"
                color="white"
                onClick={() => generateImages("Upscale")}
                minH="50px"
                w="100%"
                border="1px solid white"
                borderRadius="12px"
                isDisabled={generatedImage.upscaled}
                fontSize={{ base: "14px", md: "16px", "2xl": "20px" }}
                _hover={
                  generatedImage.upscaled
                    ? {}
                    : { opacity: 0.9, bg: "gray.600" }
                }
              >
                {generatedImage.upscaled
                  ? "Upscaled"
                  : `Upscale HD (2 credits)`}
              </Button>
            )}
          </>
        )}
      </Flex>
    );
  };

  const renderMainKeys = () => {
    if (!checkpoints) return null;

    return (
      <Flex w="100%" align="start" direction="column">
        <Text color="#D5D5D5" fontSize="16px" fontWeight="bold" mb={4}>
          Style
        </Text>
        {isMobile ? (
          <Flex gap={2} flexWrap="wrap" maxW="100%">
            {checkpoints.map((checkpoint: Checkpoint, index: number) => (
              <FilterOption
                key={index}
                bgColor="linear-gradient(to right, #FFB5FF, #1AA5D3)"
                text={formatStyle(checkpoint.style.name)}
                selected={selectedMainKey === checkpoint.style.name}
                onClick={() => handleMainKeySelection(checkpoint.style.name)}
              />
            ))}
          </Flex>
        ) : (
          <Grid
            display={{ base: "grid", md: "flex" }}
            templateColumns={{ base: `repeat(${checkpoints.length}, 1fr)` }}
            gap={2}
            overflowX={{ base: "auto", md: "visible" }}
          >
            {checkpoints.map((checkpoint: Checkpoint, index: number) => (
              <FilterOption
                key={index}
                bgColor="linear-gradient(to right, #FFB5FF, #1AA5D3)"
                text={formatStyle(checkpoint.style.name)}
                selected={selectedMainKey === checkpoint.style.name}
                onClick={() => handleMainKeySelection(checkpoint.style.name)}
              />
            ))}
          </Grid>
        )}
      </Flex>
    );
  };

  const handleNormalOptionSelection = (
    option_category_name: string,
    option_name: string
  ) => {
    if (selectedMainKey) {
      const newSelectedNormalOptions = {
        ...selectedNormalOptions,
        [option_category_name]: option_name,
      };

      if (!displayedNormalOptions[option_category_name].includes(option_name)) {
        // Replace the last displayed option with the selected one
        const newDisplayed = [...displayedNormalOptions[option_category_name]];
        newDisplayed[MAX_OPTIONS - 1] = option_name;
        setDisplayedNormalOptions({
          ...displayedNormalOptions,
          [option_category_name]: newDisplayed,
        });
      }

      setSelectedNormalOptions(newSelectedNormalOptions);
    }
  };

  const handleQuickOptionSelection = (
    option_category_name: string,
    option_name: string
  ) => {
    if (selectedMainKey) {
      const newSelectedQuickOption = {
        option_category_name: option_category_name,
        option_name: option_name,
      };

      if (!displayedQuickOptions[option_category_name].includes(option_name)) {
        // Replace the last displayed option with the selected one
        const newDisplayed = [...displayedQuickOptions[option_category_name]];
        newDisplayed[MAX_OPTIONS - 1] = option_name;
        setDisplayedQuickOptions({
          ...displayedQuickOptions,
          [option_category_name]: newDisplayed,
        });
      }

      setSelectedQuickOption(newSelectedQuickOption);
    }
  };

  const renderFilterNormalOptions = () => {
    if (!selectedCheckpoint) return null;

    return selectedCheckpoint.style.option_categories?.map(
      (option_category: OptionCategory, index: number) => {
        if (!option_category.quick_mode) {
          const optionsToShow =
            displayedNormalOptions[option_category.name] || [];

          const additionalOptions = option_category.options?.reduce(
            (accumulator: string[], option: Option) => {
              if (!optionsToShow.includes(option.name)) {
                accumulator.push(option.name);
              }

              return accumulator;
            },
            []
          );

          return (
            <Flex
              key={index}
              w="100%"
              align="start"
              direction="column"
              mt={{ base: "20px", "2xl": "40px" }}
            >
              <Text color="#D5D5D5" fontSize="16px" fontWeight="bold" mb={4}>
                {option_category.name}
              </Text>
              <Flex gap={2} flexWrap="wrap">
                {optionsToShow.map((option_name, index) => (
                  <FilterOption
                    bgColor="linear-gradient(to right, #FFB5FF, #1AA5D3)"
                    key={index}
                    text={option_name}
                    selected={
                      selectedNormalOptions[option_category.name] ===
                      option_name
                    }
                    onClick={() => {
                      if (!isWebSocketProcessing) {
                        handleNormalOptionSelection(
                          option_category.name,
                          option_name
                        );
                      }
                    }}
                  />
                ))}

                {additionalOptions !== undefined &&
                  additionalOptions.length > 0 && (
                    <Menu
                      isOpen={menuStates[option_category.name]}
                      onClose={() => toggleMenu(option_category.name)}
                    >
                      <MenuButton
                        maxH="31px"
                        p={0}
                        variant="unstyled"
                        as={Button}
                        onClick={() => toggleMenu(option_category.name)}
                      >
                        <FilterOption
                          onClick={(e) => e.stopPropagation()}
                          bgColor="linear-gradient(to right, #FFB5FF, #1AA5D3)"
                          selected={false}
                          text="More +"
                        />
                      </MenuButton>
                      <MenuList
                        p={2}
                        display="flex"
                        gap={3}
                        flexWrap="wrap"
                        w="330px"
                        bg="black"
                      >
                        {additionalOptions.map((option_name, index) => (
                          <FilterOption
                            key={index}
                            onClick={() => {
                              if (!isWebSocketProcessing) {
                                optionsToShow.push(option_name);
                                handleNormalOptionSelection(
                                  option_category.name,
                                  option_name
                                );
                                toggleMenu(option_category.name); // Close the menu after selection
                              }
                            }}
                            bgColor="linear-gradient(to right, #FFB5FF, #1AA5D3)"
                            selected={
                              selectedNormalOptions[option_category.name] ===
                              option_name
                            }
                            text={option_name}
                          />
                        ))}
                      </MenuList>
                    </Menu>
                  )}
              </Flex>
            </Flex>
          );
        }
      }
    );
  };

  const renderFilterQuickOptions = () => {
    if (!selectedCheckpoint) return null;

    return selectedCheckpoint.style.option_categories?.map(
      (option_category: OptionCategory, index: number) => {
        if (option_category.quick_mode) {
          const optionsToShow =
            displayedQuickOptions[option_category.name] || [];

          const additionalOptions = option_category.options?.reduce(
            (accumulator: string[], option: Option) => {
              if (!optionsToShow.includes(option.name)) {
                accumulator.push(option.name);
              }

              return accumulator;
            },
            []
          );

          return (
            <Flex
              key={index}
              w="100%"
              align="start"
              direction="column"
              mt={{ base: "20px", "2xl": "40px" }}
            >
              <Text color="#D5D5D5" fontSize="16px" fontWeight="bold" mb={4}>
                {option_category.name}
              </Text>
              <Flex gap={2} flexWrap="wrap">
                {optionsToShow.map((option_name, index) => (
                  <FilterOption
                    bgColor="linear-gradient(to right, #FFB5FF, #1AA5D3)"
                    key={index}
                    text={option_name}
                    selected={
                      selectedQuickOption.option_category_name ===
                        option_category.name &&
                      selectedQuickOption.option_name === option_name
                    }
                    onClick={() => {
                      if (!isWebSocketProcessing) {
                        handleQuickOptionSelection(
                          option_category.name,
                          option_name
                        );
                      }
                    }}
                  />
                ))}

                {additionalOptions !== undefined &&
                  additionalOptions.length > 0 && (
                    <Menu
                      isOpen={menuStates[option_category.name]}
                      onClose={() => toggleMenu(option_category.name)}
                    >
                      <MenuButton
                        maxH="31px"
                        p={0}
                        variant="unstyled"
                        as={Button}
                        onClick={() => toggleMenu(option_category.name)}
                      >
                        <FilterOption
                          onClick={(e) => e.stopPropagation()}
                          bgColor="linear-gradient(to right, #FFB5FF, #1AA5D3)"
                          selected={false}
                          text="More +"
                        />
                      </MenuButton>
                      <MenuList
                        p={2}
                        display="flex"
                        gap={3}
                        flexWrap="wrap"
                        w="330px"
                        bg="black"
                      >
                        {additionalOptions.map((option_name, index) => (
                          <FilterOption
                            key={index}
                            onClick={() => {
                              if (!isWebSocketProcessing) {
                                optionsToShow.push(option_name);
                                handleQuickOptionSelection(
                                  option_category.name,
                                  option_name
                                );
                                toggleMenu(option_category.name); // Close the menu after selection
                              }
                            }}
                            bgColor="linear-gradient(to right, #FFB5FF, #1AA5D3)"
                            selected={
                              selectedQuickOption.option_category_name ===
                                option_category.name &&
                              selectedQuickOption.option_name === option_name
                            }
                            text={option_name}
                          />
                        ))}
                      </MenuList>
                    </Menu>
                  )}
              </Flex>
            </Flex>
          );
        }
      }
    );
  };

  const handleDelete = () => {
    if (!selectedImage) return;
    deleteImage.mutate(
      { file_name: selectedImage.file_name },
      {
        onSuccess: () => {
          deleteClose();
          refetch();
        },
        onError: (error: any) => {
          console.error("Error deleting image:", error);
        },
      }
    );
  };

  const renderNumberOfGenerations = () => {
    const numbers = [1, 2, 4, 8];

    return (
      <Flex
        w="100%"
        align="start"
        direction="column"
        mt={{ base: "20px", "2xl": "40px" }}
      >
        <Text color="#D5D5D5" fontSize="16px" fontWeight="bold" mb={4}>
          Number of results
        </Text>
        <Flex gap={3}>
          {numbers.map((value, index) => (
            <FilterOption
              bgColor="linear-gradient(to right, #FFB5FF, #1AA5D3)"
              key={index}
              text={`${value}`}
              selected={value === numberOfGenerations}
              onClick={() => {
                if (!isWebSocketProcessing) {
                  setNumberOfGenerations(value);
                }
              }}
            />
          ))}
        </Flex>
      </Flex>
    );
  };

  const renderFixFaceToggle = () => {
    return (
      <Flex
        w="100%"
        direction="row"
        alignItems="center"
        mt={{ base: "20px", "2xl": "40px" }}
      >
        <Switch
          size="md"
          isChecked={isFixFace}
          onChange={(e) => setIsFixFace(e.target.checked)}
          isDisabled={isWebSocketProcessing}
        />
        <Text color="#D5D5D5" fontSize="16px" fontWeight="bold" ml={4}>
          Fix Face
        </Text>
      </Flex>
    );
  };

  const renderCopyPoseToggle = () => {
    return (
      <Flex
        w="100%"
        direction="row"
        alignItems="center"
        mt={{ base: "10px", "2xl": "20px" }}
      >
        <Switch
          size="md"
          isChecked={isCopyPose}
          onChange={(e) => setIsCopyPose(e.target.checked)}
          isDisabled={isWebSocketProcessing}
        />
        <Text color="#D5D5D5" fontSize="16px" fontWeight="bold" ml={4}>
          Copy Pose
        </Text>
      </Flex>
    );
  };

  const handleRatioSelect = (ratio: string) => {
    if (!isWebSocketProcessing) {
      setRatio(ratio);
    }
  };

  const renderRatioSelect = () => {
    return (
      <Flex
        w="100%"
        align="start"
        direction="column"
        mt={{ base: "20px", "2xl": "40px" }}
      >
        <Text color="#D5D5D5" fontSize="16px" fontWeight="bold" mb={4}>
          Ratio
        </Text>
        <Flex gap={3} flexWrap="wrap">
          <FilterOption
            bgColor="linear-gradient(to right, #FFB5FF, #1AA5D3)"
            key={"portrait"}
            text={"Portrait"}
            selected={ratio === "portrait"}
            onClick={() => handleRatioSelect("portrait")}
          />
          <FilterOption
            bgColor="linear-gradient(to right, #FFB5FF, #1AA5D3)"
            key={"square"}
            text={"Square"}
            selected={ratio === "square"}
            onClick={() => handleRatioSelect("square")}
          />
        </Flex>
      </Flex>
    );
  };

  const handleMainKeySelection = (key: string) => {
    if (!isWebSocketProcessing) {
      setSelectedMainKey(key);
    }
  };

  const isNormalLastLevel = () => {
    if (!selectedCheckpoint || !selectedCheckpoint.style.option_categories)
      return false;

    const normalOptionCategoriesLength =
      selectedCheckpoint.style.option_categories.filter(
        (optionCategory: OptionCategory) => !optionCategory.quick_mode
      ).length;

    if (normalOptionCategoriesLength === 0) {
      return false;
    }

    return (
      Object.keys(selectedNormalOptions).length === normalOptionCategoriesLength
    );
  };

  const BrushIcon = createIcon({
    displayName: "BrushIcon",
    viewBox: "0 0 32 32",
    path: [
      <path
        d="M28.828 3.172a4.094 4.094 0 0 0-5.656 0L4.05 22.292A6.954 6.954 0 0 0 2 27.242V30h2.756a6.952 6.952 0 0 0 4.95-2.05L28.828 8.829a3.999 3.999 0 0 0 0-5.657zM10.91 18.26l2.829 2.829l-2.122 2.121l-2.828-2.828zm-2.619 8.276A4.966 4.966 0 0 1 4.756 28H4v-.759a4.967 4.967 0 0 1 1.464-3.535l1.91-1.91l2.829 2.828zM27.415 7.414l-12.261 12.26l-2.829-2.828l12.262-12.26a2.047 2.047 0 0 1 2.828 0a2 2 0 0 1 0 2.828z"
        fill="currentColor"
      />,
      <path
        d="M6.5 15a3.5 3.5 0 0 1-2.475-5.974l3.5-3.5a1.502 1.502 0 0 0 0-2.121a1.537 1.537 0 0 0-2.121 0L3.415 5.394L2 3.98l1.99-1.988a3.585 3.585 0 0 1 4.95 0a3.504 3.504 0 0 1 0 4.949L5.439 10.44a1.502 1.502 0 0 0 0 2.121a1.537 1.537 0 0 0 2.122 0l4.024-4.024L13 9.95l-4.025 4.024A3.475 3.475 0 0 1 6.5 15z"
        fill="currentColor"
      />,
    ],
  });

  const ResetIcon = createIcon({
    displayName: "ResetIcon",
    viewBox: "0 0 32 32",
    path: (
      <path
        fill="currentColor"
        d="M7 27h23v2H7zm20.38-16.49l-7.93-7.92a2 2 0 0 0-2.83 0l-14 14a2 2 0 0 0 0 2.83L7.13 24h9.59l10.66-10.66a2 2 0 0 0 0-2.83zM15.89 22H8l-4-4l6.31-6.31l7.93 7.92zm3.76-3.76l-7.92-7.93L18 4l8 7.93z"
      />
    ),
  });

  const UndoIcon = createIcon({
    displayName: "UndoIcon",
    viewBox: "0 0 24 24",
    path: [
      <polyline
        points="1 4 1 10 7 10"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />,
      <path
        d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />,
    ],
  });

  const {
    isOpen: isInpaintOpen,
    onOpen: onInpaintOpen,
    onClose: onInpaintClose,
  } = useDisclosure();
  const [stageDimensions, setStageDimensions] = useState<StageDimensions>({
    width: 0,
    height: 0,
  });
  const stageRef = useRef<Konva.Stage | null>(null);
  const [tool, setTool] = useState<string>("brush");
  const [brushSize, setBrushSize] = useState<number>(0);
  const [cursorPosition, setCursorPosition] = useState<CursorPosition>({
    x: 0,
    y: 0,
  });
  const [isCursorInStage, setIsCursorInStage] = useState<boolean>(false);
  const isDrawing = useRef<boolean>(false);
  const [lines, setLines] = useState<LineState[]>([]);
  const [remixMaskB64DataURI, setRemixMaskB64DataURI] = useState<string | null>(
    null
  );

  const handleRemixImageClose = () => {
    if (!isWebSocketProcessing) {
      setRemixGalleryImage(null);
      setRemixImageFile(null);
      setRemixImageFileB64Plain(null);
      setRemixImagePreview(null);
      setLines([]);
      setRemixMaskB64DataURI(null);
    }
  };

  const stageImageOnLoad = (event: SyntheticEvent<HTMLImageElement>) => {
    if (
      stageDimensions.width !== event.currentTarget.offsetWidth ||
      stageDimensions.height !== event.currentTarget.offsetHeight
    ) {
      setStageDimensions({
        width: event.currentTarget.offsetWidth,
        height: event.currentTarget.offsetHeight,
      });
    }
  };

  useEffect(() => {
    setBrushSize(stageDimensions.width / 12);
  }, [stageDimensions]);

  const move = (e: MouseEvent | TouchEvent) => {
    setCursorPosition(
      e instanceof MouseEvent
        ? { x: e.clientX, y: e.clientY }
        : { x: e.touches[0].clientX || 0, y: e.touches[0].clientY || 0 }
    );
  };

  useEffect(() => {
    document.addEventListener("mousemove", move);
    document.addEventListener("touchstart", move);
    document.addEventListener("touchmove", move);

    return () => {
      document.removeEventListener("mousemove", move);
      document.removeEventListener("touchstart", move);
      document.removeEventListener("touchmove", move);
    };
  }, []);

  const handleReset = () => {
    setLines([]);
  };

  const handleUndo = () => {
    setLines((prevLines: LineState[]) =>
      prevLines.length <= 1 ? [] : prevLines.slice(0, -1)
    );
  };

  const handleCursorEnter = () => {
    setIsCursorInStage(true);
  };

  const handleCursorLeave = () => {
    isDrawing.current = false;
    setIsCursorInStage(false);
  };

  const handleCursorDown = (
    e: Konva.KonvaEventObject<MouseEvent | TouchEvent>
  ) => {
    isDrawing.current = true;
    const stage = e.target.getStage();
    if (!stage) return;
    const point = stage.getPointerPosition();
    if (!point) return;
    setLines([
      ...lines,
      { tool: tool, strokeWidth: brushSize, points: [point.x, point.y] },
    ]);

    if (e.evt instanceof TouchEvent) {
      setIsCursorInStage(true);
    }
  };

  const handleCursorMove = (
    e: Konva.KonvaEventObject<MouseEvent | TouchEvent>
  ) => {
    // no drawing - skipping
    if (!isDrawing.current) return;
    const stage = e.target.getStage();
    if (!stage) return;
    const point = stage.getPointerPosition();
    if (!point) return;
    let lastLine = lines[lines.length - 1];
    // add point
    lastLine.points = lastLine.points.concat([point.x, point.y]);
    // replace last
    lines.splice(lines.length - 1, 1, lastLine);
    setLines(lines.concat());
  };

  const handleCursorUp = (
    e: Konva.KonvaEventObject<MouseEvent | TouchEvent>
  ) => {
    isDrawing.current = false;

    if (e.evt instanceof TouchEvent) {
      setIsCursorInStage(false);
    }
  };

  const handleInpaintClose = () => {
    if (!stageRef.current) return;
    const uri = stageRef.current.toDataURL();
    setRemixMaskB64DataURI(lines.length === 0 ? null : uri);
    onInpaintClose();
  };

  return (
    <Flex
      direction="column"
      px={{
        base: "10px", 
        sm: "20px", 
        md: "30px", 
        lg: "60px", 
        xl: "60px", 
        "2xl": "20%",
      }}
      maxW="100%"
      overflow="hidden"
    >
      <Flex mt="50px" justify="center" w="100%" textAlign="center">
        <Text
          style={{
            background: "linear-gradient(to right, #FFE1FF 0%, #A4DBEE 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            MozBackgroundClip: "text",
          }}
          fontSize={{ base: "38px", lg: "56px" }}
          fontWeight="bold"
          w={{ base: "100%", lg: "340px" }}
        >
          Playground
        </Text>
      </Flex>

      {/* Mode selection area */}
      <Flex
        mt="25px"
        borderRadius="20px"
        w="100%"
        bg="transparent"
        direction="column"
        px={{ base: "15px", md: "25px" }}
        py="18px"
        border="1px solid gray"
      >
        <Text color="white" fontWeight={600}>
          Mode
        </Text>
        <Flex 
          mt="10px" 
          flexWrap={{ base: "wrap", md: "nowrap" }}
          gap={{ base: 2, md: 0 }}
        >
          <Button
            style={{
              background:
                creationMode === "Normal"
                  ? "linear-gradient(to right, #FFB5FF, #1AA5D3)"
                  : "transparent",
              color: creationMode === "Normal" ? "black" : "#F5F5F5", // Optional: Change text color when selected
              border: creationMode === "Normal" ? 0 : "2px solid gray",
            }}
            borderRadius="32px"
            onClick={() => handleCreationMode("Normal")}
            size={{ base: "sm", md: "md" }}
            minW={{ base: "auto", md: "initial" }}
          >
            Normal
          </Button>
          <Button
            style={{
              background:
                creationMode === "Prompt"
                  ? "linear-gradient(to right, #FFB5FF, #1AA5D3)"
                  : "transparent",
              color: creationMode === "Prompt" ? "black" : "#F5F5F5", // Optional: Change text color when selected
              border: creationMode === "Prompt" ? 0 : "2px solid gray",
            }}
            ml={["0px", "15px"]}
            borderRadius="32px"
            onClick={() => handleCreationMode("Prompt")}
            size={{ base: "sm", md: "md" }}
            minW={{ base: "auto", md: "initial" }}
          >
            Prompt
          </Button>
          <Button
            style={{
              background:
                creationMode === "Remix"
                  ? "linear-gradient(to right, #FFB5FF, #1AA5D3)"
                  : "transparent",
              color: creationMode === "Remix" ? "black" : "#F5F5F5", // Optional: Change text color when selected
              border: creationMode === "Remix" ? 0 : "2px solid gray",
            }}
            ml={["0px", "15px"]}
            borderRadius="32px"
            onClick={() => handleCreationMode("Remix")}
            size={{ base: "sm", md: "md" }}
            minW={{ base: "auto", md: "initial" }}
          >
            Remix
          </Button>
          <Flex ml={["0px", "15px"]} align="center" direction="column">
          <Button
              onClick={() => {
                window.open("https://magikface.com/?fpr=deepmode", "_blank");
              }}
              style={{
                background: "transparent",
                color: "#F5F5F5",
                border: "2px solid gray",
              }}
              borderRadius="32px"
              size={{ base: "sm", md: "md" }}
              minW={{ base: "auto", md: "initial" }}
            >
              Video
            </Button>
            <Text  onClick={() => {
                window.open("https://magikface.com/?fpr=deepmode", "_blank");
              }} 
              textDecoration="underline"
              fontWeight="700"
              fontSize={{ base: "8px", md: "10px" }}
              color="grey"
              cursor="pointer"
              >
              Magik Face
            </Text>
            </Flex>
        </Flex>
      </Flex>

      {/* Main content area */}
      <Flex mt="50px" w="100%" direction={{ base: "column", lg: "row" }} gap={{ base: "12px", lg: "16px" }}>
        {creationMode === "Normal" ? (
          <Flex
            direction="column"
            borderRadius="20px"
            p={8}
            w={{ base: "100%", lg: "49%" }}
            bg="#040506"
            border="1px solid rgba(255, 255, 255, 0.5)"
            justify="center"
            align="center"
            overflow={{ base: "hidden", lg: "initial" }}
          >
            {renderMainKeys()}
            {renderFilterNormalOptions()}

            {isNormalLastLevel() && (
              <Flex w="100%" direction="column">
                {renderRatioSelect()}
                {renderNumberOfGenerations()}
                {renderFixFaceToggle()}
                <Button
                  bg={isWebSocketProcessing ? "gray" : "button.500"}
                  color="white"
                  onClick={() => generateImages(creationMode)}
                  w="100%"
                  minH="50px"
                  mt="20px"
                  p="16px, 30px, 16px, 30px"
                  fontSize={{ base: "16px", "2xl": "20px" }}
                  isDisabled={isWebSocketProcessing}
                  _hover={
                    isWebSocketProcessing
                      ? {}
                      : { bg: "button.500", opacity: 0.9 }
                  }
                >
                  Create (
                  {`${creditsRequired} credit${creditsRequired > 1 ? "s" : ""}`}
                  )
                </Button>
              </Flex>
            )}
          </Flex>
        ) : creationMode === "Prompt" ? (
          <Flex
            direction="column"
            borderRadius="20px"
            p={8}
            w={{ base: "100%", lg: "49%" }}
            bg="#040506"
            border="1px solid rgba(255, 255, 255, 0.5)"
            justify="center"
            align="start"
            maxH={{ "2xl": "1200px" }}
          >
            <Flex direction="column">
              <Text fontWeight={600}>
                Describe what you <span style={{ color: "#FF1B6D" }}>want</span>{" "}
                to see in the image
              </Text>
              <Text fontWeight={600}>
                Must include the word{" "}
                <span style={{ color: "#FF1B6D" }}>{gender}</span>
              </Text>
            </Flex>
            <Flex w="100%" direction="column" position="relative">
              <Textarea
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                mt="20px"
                borderRadius="17px"
                minH="280px"
                placeholder={selectedMainKey === "realistic" ? "for example: realistic photo of a woman with white shirt, wet shirt, on the beach, winter day, rain, looking at the camera." : selectedMainKey === "anime" ? "for example: woman, japanese clothes, ponytail ,white hair, purple eyes, 2d illustration, anime style" : "for example: woman as cyberpunk, digital, octane render, 4K, best quality"}
                border="1px solid #818181"
                maxLength={customPromptMaxLength?.max_length ?? 0}
                isDisabled={isWebSocketProcessing}
              />
              <Text position="absolute" color="gray" bottom={2} right={2}>{`${
                customPrompt.length
              }/${customPromptMaxLength?.max_length ?? 0}`}</Text>

            </Flex>
            <LoraSelector isFlux={false} onSelect={handleLoraSelect} />

            <Flex
              direction="row"
              w="100%"
              alignItems="center"
              mt={{ base: "20px", "2xl": "40px" }}
            >
              <Text color="#D5D5D5" fontSize="16px" fontWeight="bold">
                Negative prompt
              </Text>
              <Button
                bg="transparent"
                color="white"
                size="sm"
                _hover={{}}
                _active={{}}
                onClick={() =>
                  setCustomNegativePromptOpen(
                    (customNegativePromptOpen) => !customNegativePromptOpen
                  )
                }
              >
                {`${customNegativePromptOpen ? "-" : "+"}`}
              </Button>
            </Flex>
            {customNegativePromptOpen && (
              <Flex w="100%" direction="column" position="relative">
                <Textarea
                  value={customNegativePrompt}
                  onChange={(e) => setCustomNegativePrompt(e.target.value)}
                  mt="10px"
                  borderRadius="17px"
                  minH="180px"
                  border="1px solid #818181"
                  maxLength={customPromptMaxLength?.max_length ?? 0}
                  isDisabled={isWebSocketProcessing}
                />
                <Text position="absolute" color="gray" bottom={2} right={2}>{`${
                  customNegativePrompt.length
                }/${customPromptMaxLength?.max_length ?? 0}`}</Text>
              </Flex>
            )}
            <Flex w="100%" direction="column">
              {renderRatioSelect()}
              {renderNumberOfGenerations()}
              {renderFixFaceToggle()}
              <Button
                bg={isWebSocketProcessing ? "gray" : "button.500"}
                color="white"
                onClick={() => generateImages(creationMode)}
                w="100%"
                minH="50px"
                mt="20px"
                p="16px, 30px, 16px, 30px"
                fontSize={{ base: "16px", "2xl": "20px" }}
                isDisabled={isWebSocketProcessing}
                _hover={
                  isWebSocketProcessing
                    ? {}
                    : { bg: "button.500", opacity: 0.9 }
                }
              >
                Create (
                {`${creditsRequired} credit${creditsRequired > 1 ? "s" : ""}`})
              </Button>
            </Flex>
          </Flex>
        ) : creationMode === "Remix" ? (
          <Flex
            direction="column"
            borderRadius="20px"
            p={8}
            w={{ base: "100%", lg: "49%" }}
            bg="#040506"
            border="1px solid rgba(255, 255, 255, 0.5)"
            justify="start"
            align="center"
            maxH={{ "2xl": "1950px" }}
            minH={{ "2xl": "710px" }}
          >
            {remixImagePreview ? (
              <>
                <Flex direction="row" w="100%" justifyContent="space-between">
                  <Button
                    _hover={
                      isWebSocketProcessing
                        ? {}
                        : { opacity: 0.9, bg: "gray.600" }
                    }
                    minH="50px"
                    minW="115px"
                    bg="transparent"
                    color="white"
                    fontSize="18px"
                    fontWeight="medium"
                    border="1px solid white"
                    borderRadius="12px"
                    onClick={handleRemixImageClose}
                    isDisabled={isWebSocketProcessing}
                    leftIcon={
                      <Icon
                        as={IoMdClose}
                        boxSize={6}
                        color="white"
                        bg="transparent"
                      />
                    }
                  >
                    Close
                  </Button>
                  <Button
                    _hover={
                      isWebSocketProcessing
                        ? {}
                        : { opacity: 0.9, bg: "gray.600" }
                    }
                    minH="50px"
                    minW="115px"
                    bg="transparent"
                    color="white"
                    fontSize="18px"
                    fontWeight="medium"
                    border="1px solid white"
                    borderRadius="12px"
                    onClick={onInpaintOpen}
                    isDisabled={isWebSocketProcessing}
                    leftIcon={
                      <BrushIcon boxSize={4} color="white" bg="transparent" />
                    }
                  >
                    Select
                  </Button>
                  <Modal
                    isOpen={isInpaintOpen}
                    onClose={handleInpaintClose}
                    size="full"
                    scrollBehavior="inside"
                  >
                    <ModalOverlay />
                    <ModalContent>
                      <ModalBody bg="#0A0E1D">
                        <Flex
                          w="100%"
                          direction="column"
                          alignItems="center"
                          onDragStart={(e) => e.preventDefault()}
                        >
                          <Flex
                            w="100%"
                            direction="row-reverse"
                            position="sticky"
                            top="10px"
                            alignItems="center"
                          >
                            <CloseButton
                              onClick={handleInpaintClose}
                              size="lg"
                            />
                          </Flex>
                          <Flex
                            w={{ base: "100%", lg: "57%", xl: "37%" }}
                            direction="column"
                            alignItems="center"
                            position="relative"
                          >
                            <Text
                              bg="linear-gradient(to right,  #FFF0E1 34%, #A4DBEE 100%)"
                              bgClip="text"
                              color="transparent"
                              fontSize={{ base: "48px", lg: "72px" }}
                              fontWeight="600"
                              mb={{ base: "15px", lg: "30px" }}
                            >
                              Select Inpaint
                            </Text>
                            <Text
                              color="white"
                              mb={{ base: "30px", lg: "60px" }}
                              fontSize={{ base: "14px", lg: "16px" }}
                            >
                              Select area for generation
                            </Text>
                            <Flex
                              w="100%"
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                              gap={{ base: 2, lg: 4 }}
                            >
                              <Button
                                _hover={{ opacity: 0.9, bg: "gray.600" }}
                                _active={{ opacity: 0.9, bg: "gray.600" }}
                                minH="50px"
                                minW={{ lg: "130px" }}
                                size={{ base: "xs", lg: "md" }}
                                bg="transparent"
                                color="white"
                                fontSize={{ base: "14px", lg: "18px" }}
                                fontWeight="medium"
                                border="1px solid white"
                                borderRadius={0}
                                onClick={handleReset}
                                leftIcon={
                                  <ResetIcon
                                    boxSize={{ base: 3, lg: 4 }}
                                    color="white"
                                    bg="transparent"
                                  />
                                }
                              >
                                Reset
                              </Button>
                              <Button
                                _hover={{ opacity: 0.9, bg: "gray.600" }}
                                _active={{ opacity: 0.9, bg: "gray.600" }}
                                minH="50px"
                                minW={{ lg: "130px" }}
                                size={{ base: "xs", lg: "md" }}
                                bg="transparent"
                                color="white"
                                fontSize={{ base: "14px", lg: "18px" }}
                                fontWeight="medium"
                                border="1px solid white"
                                borderRadius={0}
                                onClick={handleUndo}
                                leftIcon={
                                  <UndoIcon
                                    boxSize={{ base: 3, lg: 4 }}
                                    color="white"
                                    bg="transparent"
                                  />
                                }
                              >
                                Undo
                              </Button>
                              <Slider
                                step={1}
                                min={0}
                                max={stageDimensions.width / 6}
                                value={brushSize}
                                onChange={(value) =>
                                  setBrushSize(value >= 1 ? value : 1)
                                }
                                maxW="35%"
                              >
                                <SliderTrack
                                  bg="white"
                                  boxSize={2}
                                  borderRadius={4}
                                >
                                  <SliderFilledTrack bg="button.500" />
                                </SliderTrack>
                                <SliderThumb bg="white" boxSize={4} />
                              </Slider>
                              <Menu matchWidth={true}>
                                <MenuButton
                                  as={Button}
                                  _hover={{ opacity: 0.9, bg: "gray.600" }}
                                  _active={{ opacity: 0.9, bg: "gray.600" }}
                                  minH="50px"
                                  minW={{ lg: "130px" }}
                                  size={{ base: "xs", lg: "md" }}
                                  bg="transparent"
                                  color="white"
                                  fontSize={{ base: "14px", lg: "18px" }}
                                  fontWeight="medium"
                                  border="1px solid white"
                                  borderRadius={0}
                                  leftIcon={
                                    tool === "eraser" ? (
                                      <Icon
                                        as={BsEraser}
                                        boxSize={{ base: 3, lg: 4 }}
                                        color="white"
                                        bg="transparent"
                                      />
                                    ) : (
                                      <BrushIcon
                                        boxSize={{ base: 3, lg: 4 }}
                                        color="white"
                                        bg="transparent"
                                      />
                                    )
                                  }
                                >
                                  {tool === "eraser" ? "Eraser" : "Brush"}
                                </MenuButton>
                                <MenuList
                                  border="1px solid white"
                                  borderRadius={0}
                                  bg="#0A0E1D"
                                  minWidth="fit-content"
                                >
                                  <MenuItem
                                    bg="#0A0E1D"
                                    _hover={{ bg: "#1B1F2E" }}
                                    onClick={() => setTool("brush")}
                                  >
                                    <Flex
                                      w="100%"
                                      direction="row"
                                      justifyContent="center"
                                      alignItems="center"
                                    >
                                      <Text
                                        color="white"
                                        fontSize={{ base: "14px", lg: "18px" }}
                                        fontWeight="medium"
                                      >
                                        Brush
                                      </Text>
                                    </Flex>
                                  </MenuItem>
                                  <MenuItem
                                    bg="#0A0E1D"
                                    _hover={{ bg: "#1B1F2E" }}
                                    onClick={() => setTool("eraser")}
                                  >
                                    <Flex
                                      w="100%"
                                      direction="row"
                                      justifyContent="center"
                                      alignItems="center"
                                    >
                                      <Text
                                        color="white"
                                        fontSize={{ base: "14px", lg: "18px" }}
                                        fontWeight="medium"
                                      >
                                        Eraser
                                      </Text>
                                    </Flex>
                                  </MenuItem>
                                </MenuList>
                              </Menu>
                            </Flex>
                            <Flex
                              w="100%"
                              position="relative"
                              mt={{ base: "10px", lg: "20px" }}
                              cursor="none"
                            >
                              <Image
                                src={remixImagePreview}
                                w="100%"
                                onLoad={stageImageOnLoad}
                                userSelect="none"
                                onDragStart={(e) => e.preventDefault()}
                              />
                              <Flex
                                w="100%"
                                position="absolute"
                                top="0"
                                left="0"
                                opacity={0.5}
                              >
                                <Stage
                                  width={stageDimensions.width}
                                  height={stageDimensions.height}
                                  onMouseEnter={handleCursorEnter}
                                  onMouseLeave={handleCursorLeave}
                                  onMouseDown={handleCursorDown}
                                  onMousemove={handleCursorMove}
                                  onMouseup={handleCursorUp}
                                  onTouchStart={handleCursorDown}
                                  onTouchMove={handleCursorMove}
                                  onTouchEnd={handleCursorUp}
                                  ref={stageRef}
                                >
                                  <Layer>
                                    {lines.map((line, i) =>
                                      line.points.length === 2 ? (
                                        <Circle
                                          key={i}
                                          x={line.points[0]}
                                          y={line.points[1]}
                                          radius={line.strokeWidth / 2}
                                          fill="white"
                                          globalCompositeOperation={
                                            line.tool === "eraser"
                                              ? "destination-out"
                                              : "source-over"
                                          }
                                        />
                                      ) : (
                                        <Line
                                          key={i}
                                          points={line.points}
                                          stroke="white"
                                          strokeWidth={line.strokeWidth}
                                          tension={0.5}
                                          lineCap="round"
                                          lineJoin="round"
                                          globalCompositeOperation={
                                            line.tool === "eraser"
                                              ? "destination-out"
                                              : "source-over"
                                          }
                                        />
                                      )
                                    )}
                                  </Layer>
                                </Stage>
                              </Flex>
                              {isCursorInStage && (
                                <div
                                  style={{
                                    position: "fixed",
                                    backgroundColor: "white",
                                    opacity: 0.5,
                                    height: brushSize,
                                    width: brushSize,
                                    borderRadius: "50%",
                                    transform: "translate(-50%, -50%)",
                                    pointerEvents: "none",
                                    transition: "background-color 0.2s ease",
                                    left: cursorPosition.x,
                                    top: cursorPosition.y,
                                  }}
                                >
                                  <div
                                    style={{
                                      backgroundColor: "gray",
                                      height: "15%",
                                      width: "15%",
                                      minHeight: "1px",
                                      minWidth: "1px",
                                      borderRadius: "50%",
                                      marginLeft: "50%",
                                      marginTop: "50%",
                                      transform: "translate(-50%, -50%)",
                                      pointerEvents: "none",
                                      transition: "background-color 0.2s ease",
                                    }}
                                  />
                                </div>
                              )}
                            </Flex>
                            <Button
                              bg="button.500"
                              color="white"
                              onClick={handleInpaintClose}
                              w="100%"
                              minH="50px"
                              mt={{ base: "20px", lg: "40px" }}
                              p="16px, 30px, 16px, 30px"
                              fontSize={{ base: "16px", "2xl": "20px" }}
                              _hover={{ bg: "button.500", opacity: 0.9 }}
                            >
                              Confirm
                            </Button>
                          </Flex>
                        </Flex>
                      </ModalBody>
                    </ModalContent>
                  </Modal>
                </Flex>
                <Flex
                  w="100%"
                  aspectRatio={1}
                  position="relative"
                  marginTop={{ base: "15px", lg: "32px" }}
                >
                  <Image
                    src={remixImagePreview}
                    objectFit="contain"
                    w="100%"
                    aspectRatio={1}
                    border="1px solid #68696E"
                    borderRadius="12px"
                    userSelect="none"
                    onDragStart={(e) => e.preventDefault()}
                  />
                  {remixMaskB64DataURI && (
                    <Image
                      src={remixMaskB64DataURI}
                      objectFit="contain"
                      w="100%"
                      aspectRatio={1}
                      position="absolute"
                      top="0"
                      left="0"
                      opacity={0.5}
                      userSelect="none"
                      onDragStart={(e) => e.preventDefault()}
                    />
                  )}
                </Flex>
              </>
            ) : (
              <div
                {...getRootProps()}
                style={{
                  width: "100%",
                  aspectRatio: 1,
                  margin: "20px 0px 20px 0px",
                  border: "1px solid #68696E",
                  borderRadius: "12px",
                  padding: "20px",
                  textAlign: "center",
                }}
              >
                <input {...getInputProps()} disabled={isWebSocketProcessing} />
                <Flex
                  direction="column"
                  w="100%"
                  h="100%"
                  align="center"
                  justify="center"
                >
                  {isDragActive ? (
                    <Text> Drop your image here!</Text>
                  ) : (
                    <>
                      <Image
                        src={UploadRemixImageIcon.src}
                        objectFit="contain"
                      />
                      <Text
                        mt="20px"
                        fontSize="18px"
                        color="white"
                        opacity="50%"
                      >
                        Drag & Drop or Paste
                      </Text>
                      <Button
                        mt="20px"
                        _hover={
                          isWebSocketProcessing
                            ? {}
                            : { opacity: 0.9, bg: "gray.600" }
                        }
                        minH="50px"
                        minW="300px"
                        bg="transparent"
                        color="white"
                        fontSize="18px"
                        border="1px solid white"
                        borderRadius="12px"
                        isDisabled={isWebSocketProcessing}
                      >
                        Upload Image
                      </Button>
                      <Button
                        mt="10px"
                        _hover={
                          isWebSocketProcessing
                            ? {}
                            : { opacity: 0.9, bg: "gray.600" }
                        }
                        minH="50px"
                        minW="300px"
                        bg="transparent"
                        color="white"
                        fontSize="18px"
                        border="1px solid white"
                        borderRadius="12px"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemixGalleryOpen();
                        }}
                        isDisabled={isWebSocketProcessing}
                      >
                        From Gallery
                      </Button>
                      <RemixGalleryLayout
                        isAdmin={false}
                        isOpen={isRemixGalleryOpen}
                        onClose={onRemixGalleryClose}
                        selectedImage={remixGalleryImage}
                        setSelectedImage={(image: RemixGalleryImage | null) => {
                          if (!isWebSocketProcessing) {
                            setRemixGalleryImage(image);
                          }
                        }}
                      />
                    </>
                  )}
                </Flex>
              </div>
            )}
            {remixImagePreview !== null && (
              <>
                <Flex
                  w="100%"
                  align="start"
                  direction="column"
                  mt={{ base: "20px", "2xl": "40px" }}
                >
                      <LoraSelector isFlux={false} onSelect={handleLoraSelect} />

                  <Text
                    color="#D5D5D5"
                    fontSize="16px"
                    fontWeight="bold"
                    mb="6"
                    mt="6"
                  >
                    Similarity
                  </Text>
                  <Slider
                    step={0.1}
                    min={0}
                    max={1}
                    value={similarity}
                    onChange={(value) =>
                      setSimilarity(value >= 0.1 ? value : 0.1)
                    }
                    isDisabled={isWebSocketProcessing}
                  >
                    <SliderMark
                      value={0}
                      textAlign="left"
                      bg="transparent"
                      color="white"
                      mt="3"
                      fontSize="sm"
                    >
                      My Model
                    </SliderMark>
                    <SliderMark
                      value={1}
                      textAlign="right"
                      bg="transparent"
                      color="white"
                      mt="3"
                      ml="-12"
                      w="12"
                      fontSize="sm"
                    >
                      Image
                    </SliderMark>
                    <SliderMark
                      value={similarity}
                      textAlign="center"
                      bg="transparent"
                      color="white"
                      mt="-10"
                      ml="-6"
                      w="12"
                      fontSize="sm"
                    >
                      {similarity}
                    </SliderMark>
                    <SliderTrack bg="white" boxSize={2} borderRadius={4}>
                      <SliderFilledTrack bg="button.500" />
                    </SliderTrack>
                    <SliderThumb bg="white" boxSize={4} />
                  </Slider>
                </Flex>
                <Flex
                  direction="row"
                  w="100%"
                  alignItems="center"
                  mt={{ base: "40px", "2xl": "80px" }}
                >
                  <Text color="#D5D5D5" fontSize="16px" fontWeight="bold">
                    Prompt (optional)
                  </Text>
                  <Button
                    bg="transparent"
                    color="white"
                    size="sm"
                    _hover={{}}
                    _active={{}}
                    onClick={() =>
                      setCustomPromptOpen(
                        (customPromptOpen) => !customPromptOpen
                      )
                    }
                  >
                    {`${customPromptOpen ? "-" : "+"}`}
                  </Button>
                </Flex>
                {customPromptOpen && (
                  <Flex w="100%" direction="column" position="relative">
                    <Textarea
                      value={customPrompt}
                      onChange={(e) => setCustomPrompt(e.target.value)}
                      mt="10px"
                      borderRadius="17px"
                      minH="180px"
                      border="1px solid #818181"
                      maxLength={customPromptMaxLength?.max_length ?? 0}
                      isDisabled={isWebSocketProcessing}
                    />
                    <Text
                      position="absolute"
                      color="gray"
                      bottom={2}
                      right={2}
                    >{`${customPrompt.length}/${
                      customPromptMaxLength?.max_length ?? 0
                    }`}</Text>
                  </Flex>
                )}
                <Flex
                  direction="row"
                  w="100%"
                  alignItems="center"
                  mt={{ base: "20px", "2xl": "40px" }}
                >
                  <Text color="#D5D5D5" fontSize="16px" fontWeight="bold">
                    Negative prompt (optional)
                  </Text>
                  <Button
                    bg="transparent"
                    color="white"
                    size="sm"
                    _hover={{}}
                    _active={{}}
                    onClick={() =>
                      setCustomNegativePromptOpen(
                        (customNegativePromptOpen) => !customNegativePromptOpen
                      )
                    }
                  >
                    {`${customNegativePromptOpen ? "-" : "+"}`}
                  </Button>
                </Flex>
                {customNegativePromptOpen && (
                  <Flex w="100%" direction="column" position="relative">
                    <Textarea
                      value={customNegativePrompt}
                      onChange={(e) => setCustomNegativePrompt(e.target.value)}
                      mt="10px"
                      borderRadius="17px"
                      minH="180px"
                      border="1px solid #818181"
                      maxLength={customPromptMaxLength?.max_length ?? 0}
                      isDisabled={isWebSocketProcessing}
                    />
                    <Text
                      position="absolute"
                      color="gray"
                      bottom={2}
                      right={2}
                    >{`${customNegativePrompt.length}/${
                      customPromptMaxLength?.max_length ?? 0
                    }`}</Text>
                  </Flex>
                )}
                <Flex w="100%" direction="column">
                  {renderNumberOfGenerations()}
                  {renderFixFaceToggle()}
                  {renderCopyPoseToggle()}
                  <Button
                    bg={isWebSocketProcessing ? "gray" : "button.500"}
                    color="white"
                    onClick={() => generateImages(creationMode)}
                    w="100%"
                    minH="50px"
                    mt="20px"
                    p="16px, 30px, 16px, 30px"
                    fontSize={{ base: "16px", "2xl": "20px" }}
                    isDisabled={isWebSocketProcessing}
                    _hover={
                      isWebSocketProcessing
                        ? {}
                        : { bg: "button.500", opacity: 0.9 }
                    }
                  >
                    Remix (
                    {`${creditsRequired} credit${
                      creditsRequired > 1 ? "s" : ""
                    }`}
                    )
                  </Button>
                </Flex>
              </>
            )}
          </Flex>
        ) : (
          <Flex
            direction="column"
            borderRadius="20px"
            p={8}
            w={{ base: "100%", lg: "49%" }}
            bg="#040506"
            border="1px solid rgba(255, 255, 255, 0.5)"
            justify="center"
            align="center"
            maxH={{ "2xl": "1000px" }}
          >
            <Text
              color="#D5D5D5"
              fontSize="16px"
              fontWeight="bold"
              w="100%"
              mb={4}
            >
              {`Choose 1 template and click "Create"`}
            </Text>
            {renderMainKeys()}
            {renderFilterQuickOptions()}

            <Flex w="100%" direction="column">
              {renderNumberOfGenerations()}
              {renderFixFaceToggle()}

              <Button
                bg={isWebSocketProcessing ? "gray" : "button.500"}
                color="white"
                onClick={() => generateImages(creationMode)}
                w="100%"
                minH="50px"
                mt="20px"
                p="16px, 30px, 16px, 30px"
                fontSize={{ base: "16px", "2xl": "20px" }}
                isDisabled={isWebSocketProcessing}
                _hover={
                  isWebSocketProcessing
                    ? {}
                    : { bg: "button.500", opacity: 0.9 }
                }
              >
                Create (
                {`${creditsRequired} credit${creditsRequired > 1 ? "s" : ""}`})
              </Button>
            </Flex>
          </Flex>
        )}

        {renderImages()}
      </Flex>
      <Text mt="10px" fontWeight={700} fontSize={{ base: "14px", md: "16px" }}>
        Missing a Tag?{" "}
        <span style={{ textDecoration: "underline", fontWeight: 600 }}>
          <a target="_blank" href="https://discord.gg/CMwtccKthy">
            Join our Discord to suggest it
          </a>
        </span>
      </Text>

      {/* ... rest of the component ... */}

      <Grid
        gap={{ base: 3, md: 5 }}
        templateColumns={{
          base: `repeat(2,1fr)`,
          md: `repeat(3,1fr)`,
          xl: `repeat(${MAX_COLUMNS_XL},1fr)`,
          "2xl": `repeat(${MAX_COLUMNS_2XL},1fr)`,
        }}
        mt="20px"
        mx={{ base: "auto", md: 0 }}
        maxW="100%"
      >
        {updatedGenerations
          .filter((generation) => generation.status !== "pending" && generation.status !== "failed") 
          .slice(0, maxImagesToShow)
          .map((generation, index) => {
            const imageUrl = generation.file_name.startsWith("http")
              ? generation.file_name // Use file_name if it's a complete URL
              : generation.sas_url || ""; // Fallback to sas_url

            return (
              <Flex key={index} direction="column" position="relative" maxW="100%">
                <Flex
                  position="relative"
                  direction="column"
                  justifyContent="center"
                  alignItems="center"
                  borderRadius="14px"
                  overflow="hidden"
                  w="100%"
                  sx={{
                    aspectRatio: "3/4", // Ensures a consistent aspect ratio
                  }}
                >
                  <NsfwImage
                    key={imageUrl}
                    nsfw={generation.nsfw && !profile?.nsfw_consent}
                    //@ts-ignore
                    src={imageUrl}
                    alt="image of model"
                    onClick={() => {
                      setSelectedImageUrl(imageUrl);
                      setSelectedImage(generation);
                      openPromptModal()
                    }}
                  />
                  <Flex
                    position="absolute"
                    top={{ base: "2", md: "5" }}
                    left={{ base: "2", md: "5" }}
                  >
                  </Flex>
                  {generation.upscaled && (
                    <Flex
                      position="absolute"
                      top={2}
                      right={2}
                      bgColor="black"
                      w="24px"
                      h="16px"
                      justifyContent="center"
                      alignItems="center"
                      border="1px solid yellow"
                      borderRadius="2px"
                    >
                      <Text
                        w="100%"
                        fontSize="12px"
                        fontWeight="bold"
                        color="yellow"
                        textAlign="center"
                      >
                        HD
                      </Text>
                    </Flex>
                  )}
                  <Button
                    variant="unstyled"
                    bg="red"
                    w="32px"
                    h="32px"
                    p={0}
                    position="absolute"
                    bottom={{ base: "2", md: "5" }}
                    right={{ base: "2", md: "5" }}
                    sx={{
                      minWidth: "32px !important",
                      minHeight: "32px !important",
                      width: "32px !important",
                      height: "32px !important",
                      padding: "0 !important",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImage(generation);
                      deleteOpen();
                    }}
                  >
                    <Icon as={FaTrash} boxSize={4} />
                  </Button>
                </Flex>
              </Flex>
            );
          })}
{ selectedImage &&                   
<PromptModal isOpen={isPromptModalOpen} onClose={closePromptModal} isFlux={false} model_id={id} generationImage={selectedImage.sas_url} file_name={selectedImage.file_name} />
}

        <Button
          onClick={() => router.push(`/creations/${id}`)}
          borderRadius="15px"
          bg="transparent"
          border="1px solid white"
        >
          <Text color="white">Creation History</Text>
        </Button>

        <Modal size="xl" isOpen={isDeleteOpen} onClose={deleteClose}>
          <ModalOverlay />
          <ModalContent borderRadius={0} bg="#16181F" border="1px solid white">
            <ModalCloseButton />
            <Flex
              justify="center"
              w="100%"
              p={10}
              px={{ base: "30px", md: "80px" }}
              minH="300px"
              direction="column"
            >
              <Text fontSize={{ base: "28px", md: "36px" }} fontWeight={600}>
                Are you sure you want to delete this image?
              </Text>
              <Flex>
                <Button
                  mt="55px"
                  bg="white"
                  borderRadius="32px"
                  color="black"
                  onClick={handleDelete}
                >
                  Delete Image
                </Button>
              </Flex>
            </Flex>
          </ModalContent>
        </Modal>
      </Grid>

      <InsufficientCreditsModal isOpen={isOpen} onClose={onClose} />

    </Flex>
  );
};

// Define the types for the component's props
type InsufficientCreditsModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const InsufficientCreditsModal: React.FC<
  InsufficientCreditsModalProps
> = ({ isOpen, onClose }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader color="black">Insufficient Credits</ModalHeader>
        <ModalCloseButton />
        <ModalBody color="black">
          <p>You do not have enough credits to continue.</p>
          <Button colorScheme="blue" onClick={onClose}>
            Close
          </Button>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default Generator;
